
import { Vue, Component, Watch } from "vue-property-decorator";
import StockOrderModule from "@/store/modules/barcodeSystem/stockOrder";
import BarcodeCompanyModule from "@/store/modules/barcodeSystem/barcodeCompany";
import BarcodeManufacturerModule from "@/store/modules/barcodeSystem/barcodeManufacturer";
import StockSkuModule from "@/store/modules/barcodeSystem/stockSku";
import { getModule } from "vuex-module-decorators";
import { StockSkuResource } from "../../models/api/barcodeSystem/stockSkus/get";
import { CreateStockOrderRequest } from "../../models/api/barcodeSystem/stockOrders/create";
import { CompanyBillToResource } from "../../models/api/barcodeSystem/barcodeCompanies/billTos";
import notifications from "../../lib/notifications";
import Select from "../../components/form/Select.vue";

const stockOrderModuleState = getModule(StockOrderModule);
const barcodeCompanyModuleState = getModule(BarcodeCompanyModule);
const barcodeManufacturerModuleState = getModule(BarcodeManufacturerModule);
const stockSkuModuleState = getModule(StockSkuModule);

interface StockOrderLineItem {
  lineNumber: number;
  stockSkuId: number;
  sku: string;
  itemDescription: string;
  commodity: string;
  isBulkItem: boolean;
  quantity: number;
  serialNumber: string;
  barcode: string;
  poNumber: string;
}

@Component({
  components: {
    Select
  }
})
export default class NewStockOrder extends Vue {
  companyId: number = null;
  stockOrderName: string = null;
  shipDate: string = null;
  anticipatedReceiveDate: string = null;
  billToCompanyId: string = null;
  companyBillTos: CompanyBillToResource[] = [];
  facilityCode: string = null;
  shippingFacility: string = null;
  saving: boolean = false;

  items: StockOrderLineItem[] = [];

  showAddItemModal: boolean = false;
  addItemStep: string = 'select';
  skuSearchFilter: string = null;
  selectedSku: StockSkuResource = null;
  itemQuantity: number = 1;
  itemSerialNumber: string = null;
  itemBarcode: string = null;
  itemPoNumber: string = null;

  @Watch('companyId')
  async onCompanyIdChanged(newVal: number) {
    this.billToCompanyId = null;
    this.companyBillTos = [];
    if (this.items.length > 0) {
      this.items = [];
    }
    if (newVal) {
      const company = (barcodeCompanyModuleState.companies || []).find(x => x.id === newVal);
      if (!company || !company.facilityCodes.includes(this.facilityCode)) {
        this.facilityCode = null;
      }
      await this.loadBillToForCompany(newVal);
      await this.loadStockSkusForCompany(newVal);
    } else {
      this.facilityCode = null;
    }
  }

  get billToOptions() {
    return this.companyBillTos.map(x => ({ label: x.billToCompanyId, value: x.billToCompanyId }));
  }

  async loadBillToForCompany(companyId: number) {
    try {
      const billTos = await barcodeCompanyModuleState.getCompanyBillTos(companyId);
      if (this.companyId !== companyId) return;
      this.companyBillTos = billTos || [];
      this.billToCompanyId = this.companyBillTos.length === 1 ? this.companyBillTos[0].billToCompanyId : null;
    } catch (ex) {
      notifications.error("Load Bill-To", "Failed to load bill-to for company", ex);
    }
  }

  async loadStockSkusForCompany(companyId: number) {
    try {
      await stockSkuModuleState.loadStockSkus({ companyId: companyId });
    } catch (ex) {
      notifications.error("Load Stock SKUs", "Failed to load stock SKUs for company", ex);
    }
  }

  get availableStockSkus(): StockSkuResource[] {
    const skus = stockSkuModuleState.stockSkus || [];
    const filterText = (this.skuSearchFilter || '').toUpperCase().trim();
    const usedIds = this.items.map(x => x.stockSkuId);
    const allowedManufacturerCodes = (barcodeManufacturerModuleState.manufacturers || [])
      .filter(x => x.allowManualStockOrdersCreation)
      .map(x => x.code);

    return skus.filter(x => allowedManufacturerCodes.includes(x.manufacturerCode) && !usedIds.includes(x.id) && (!filterText ||
      x.sku.toUpperCase().includes(filterText) ||
      (x.itemDescription && x.itemDescription.toUpperCase().includes(filterText)) ||
      ((x.commodity || x.itemType) && (x.commodity || x.itemType).toUpperCase().includes(filterText))));
  }

  get totalUnits() {
    return this.items.reduce((sum, item) => sum + (item.quantity || 0), 0);
  }

  get canAddItem() {
    return !!this.companyId;
  }

  get canSave() {
    return !!(this.companyId && this.stockOrderName && this.billToCompanyId && this.facilityCode && this.items.length > 0) && !this.saving;
  }

  formatLineNumber(lineNumber: number) {
    const value = lineNumber.toString();
    return value.length >= 3 ? value : "000".substr(0, 3 - value.length) + value;
  }

  openAddItemModal() {
    this.addItemStep = 'select';
    this.skuSearchFilter = null;
    this.selectedSku = null;
    this.showAddItemModal = true;
  }

  selectSku(sku: StockSkuResource) {
    this.selectedSku = sku;
    this.itemQuantity = 1;
    this.itemSerialNumber = null;
    this.itemBarcode = null;
    this.itemPoNumber = null;
    this.addItemStep = 'details';
  }

  backToSkuSelect() {
    this.addItemStep = 'select';
    this.selectedSku = null;
  }

  addSelectedItemToOrder() {
    if (!this.selectedSku || !this.itemQuantity || this.itemQuantity < 1) return;

    this.items.push({
      lineNumber: this.items.length + 1,
      stockSkuId: this.selectedSku.id,
      sku: this.selectedSku.sku,
      itemDescription: this.selectedSku.itemDescription,
      commodity: this.selectedSku.commodity || this.selectedSku.itemType,
      isBulkItem: this.selectedSku.isBulkItem,
      quantity: this.itemQuantity,
      serialNumber: this.selectedSku.isBulkItem ? null : this.itemSerialNumber,
      barcode: this.selectedSku.isBulkItem ? null : this.itemBarcode,
      poNumber: this.selectedSku.isBulkItem ? null : this.itemPoNumber
    });

    this.showAddItemModal = false;
  }

  removeItem(index: number) {
    this.items.splice(index, 1);
    this.items.forEach((item, idx) => item.lineNumber = idx + 1);
  }

  async saveOrder() {
    if (!this.canSave) return;

    const request: CreateStockOrderRequest = {
      companyId: this.companyId,
      crn: this.stockOrderName,
      shipDate: this.shipDate,
      anticipatedReceiveDate: this.anticipatedReceiveDate,
      billToCompanyId: this.billToCompanyId,
      facilityCode: this.facilityCode,
      shippingFacility: this.shippingFacility,
      items: this.items.map(x => ({
        stockSkuId: x.stockSkuId,
        quantity: x.quantity,
        serialNumber: x.serialNumber,
        barcode: x.barcode,
        poNumber: x.poNumber
      }))
    };

    this.saving = true;
    try {
      await stockOrderModuleState.createStockOrder(request);
      notifications.success("Stock Order Created", "Successfully created stock order");
      this.$router.push({ path: "/barcode/stockOrderSearch" });
    } catch (ex) {
      notifications.error("Create Stock Order", "Failed to create stock order", ex);
    }
    this.saving = false;
  }

  clearForm() {
    this.companyId = null;
    this.stockOrderName = null;
    this.shipDate = null;
    this.anticipatedReceiveDate = null;
    this.billToCompanyId = null;
    this.companyBillTos = [];
    this.facilityCode = null;
    this.shippingFacility = null;
    this.items = [];
  }

  cancel() {
    this.$router.push({ path: "/barcode/stockOrderSearch" });
  }
}
